import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscribeLeaderboards } from '../../services/leaderboardService';
import { useAuth } from '../../contexts/AuthContext';
import { CATEGORIES } from '../../constants';
import {
    ArrowLeftIcon,
    StarIcon,
    ThumbDownIcon,
    DiamondIcon,
    ChartIcon,
    ClockIcon,
    UserIcon
} from '../icons';
import './LeaderboardPage.css';

// Reusable Row Component
const LeaderboardRow = ({ rank, image, name, subtitle, score, type = 'standard', isUser = false, icon }) => {
    const isTop3 = rank < 3;

    return (
        <div className={`leaderboard-row rank-${rank + 1} ${isTop3 ? 'top-rank' : ''} ${isUser ? 'is-user' : ''} type-${type}`}>
            <div className="row-rank">
                {rank + 1}
            </div>

            <div className="row-avatar">
                {image ? (
                    <img src={image} alt={name} loading="lazy" />
                ) : (
                    <div className="avatar-placeholder">{icon}</div>
                )}
                {isTop3 && <div className="rank-glow"></div>}
            </div>

            <div className="row-content">
                <h3 className="row-name">
                    {name}
                    {isUser && <span className="you-tag">You</span>}
                </h3>
                <div className="row-subtitle">{subtitle}</div>
            </div>

            <div className={`row-score ${score < 0 ? 'negative' : 'positive'}`}>
                <div className="score-value">
                    {score > 0 ? '+' : ''}{score.toLocaleString()}
                </div>
                <div className="score-label">Karma</div>
            </div>
        </div>
    );
};

function LeaderboardPage({ persons = [] }) {
    const [leaderboards, setLeaderboards] = useState({});
    const [activeTab, setActiveTab] = useState('fame');
    const [activeCategory, setActiveCategory] = useState('Politics');
    const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();

    useEffect(() => {
        const unsubscribe = subscribeLeaderboards((data) => {
            setLeaderboards(data || {});
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const formatLastUpdated = (timestamp) => {
        if (!timestamp) return 'Never';
        return new Date(timestamp).toLocaleString();
    };

    if (loading) {
        return (
            <div className="leaderboard-page">
                <div className="leaderboard-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading leaderboards...</p>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'fame', label: 'Hall of Fame', icon: <StarIcon /> },
        { id: 'shame', label: 'Hall of Shame', icon: <ThumbDownIcon /> },
        { id: 'donors', label: 'Top Donors', icon: <DiamondIcon /> },
        { id: 'categories', label: 'By Category', icon: <ChartIcon /> },
    ];

    return (
        <div className="leaderboard-page">
            <div className="leaderboard-header">
                <div className="header-top-row">
                    <Link to="/" className="back-link">
                        <ArrowLeftIcon />
                        <span>Back to Rankings</span>
                    </Link>
                    <div className="last-updated">
                        <ClockIcon />
                        <span>Updated: {formatLastUpdated(leaderboards.lastUpdated)}</span>
                    </div>
                </div>

                <div className="header-title-group">
                    <h1>Leaderboards</h1>
                </div>

                <div className="leaderboard-tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="leaderboard-content">
                {/* Hall of Fame */}
                {activeTab === 'fame' && (
                    <div className="leaderboard-section full-page hall-of-fame">
                        <div className="section-header center">
                            <h2>Most Loved Personalities</h2>
                            <span className="section-subtitle">The people making the world better</span>
                        </div>
                        <div className="leaderboard-list">
                            {(leaderboards.hallOfFame || []).length === 0 ? (
                                <p className="empty-message">No data yet. Vote to see rankings!</p>
                            ) : (
                                leaderboards.hallOfFame.map((person, index) => (
                                    <LeaderboardRow
                                        key={person.id}
                                        rank={index}
                                        name={person.name}
                                        subtitle={person.category}
                                        score={person.votes}
                                        image={person.image}
                                        type="fame"
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Hall of Shame */}
                {activeTab === 'shame' && (
                    <div className="leaderboard-section full-page hall-of-shame">
                        <div className="section-header center">
                            <h2>Most Disliked Personalities</h2>
                            <span className="section-subtitle">The ones needing some karma adjustment</span>
                        </div>
                        <div className="leaderboard-list">
                            {(leaderboards.hallOfShame || []).length === 0 ? (
                                <p className="empty-message">No data yet. Vote to see rankings!</p>
                            ) : (
                                leaderboards.hallOfShame.map((person, index) => (
                                    <LeaderboardRow
                                        key={person.id}
                                        rank={index}
                                        name={person.name}
                                        subtitle={person.category}
                                        score={person.votes}
                                        image={person.image}
                                        type="shame"
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Top Donors */}
                {activeTab === 'donors' && (
                    <div className="leaderboard-section full-page top-donors">
                        <div className="section-header center">
                            <h2>Top Karma Donors</h2>
                            <span className="section-subtitle">Our most active community members</span>
                        </div>
                        <div className="leaderboard-list">
                            {(leaderboards.topDonors || []).length === 0 ? (
                                <p className="empty-message">No data yet. Start voting!</p>
                            ) : (
                                leaderboards.topDonors.map((donor, index) => (
                                    <LeaderboardRow
                                        key={donor.voterId}
                                        rank={index}
                                        name={donor.displayName || 'Anonymous'}
                                        subtitle={`${donor.totalVotes} votes cast`}
                                        score={donor.totalVotes}
                                        image={donor.photoURL}
                                        isUser={currentUser && donor.voterId === currentUser.uid}
                                        icon={<UserIcon />}
                                        type="donor"
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Category Leaders */}
                {activeTab === 'categories' && (
                    <div className="leaderboard-section full-page category-leaders">
                        <div className="section-header center">
                            <h2>Leaders by Category</h2>
                            <span className="section-subtitle">Who's winning in each field?</span>
                        </div>
                        <div className="category-tabs">
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat}
                                    className={`category-tab ${activeCategory === cat ? 'active' : ''}`}
                                    onClick={() => setActiveCategory(cat)}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                        <div className="leaderboard-list">
                            {(leaderboards.categories?.[activeCategory] || []).length === 0 ? (
                                <p className="empty-message">No data for {activeCategory} yet.</p>
                            ) : (
                                leaderboards.categories[activeCategory].map((person, index) => (
                                    <LeaderboardRow
                                        key={person.id}
                                        rank={index}
                                        name={person.name}
                                        subtitle={activeCategory}
                                        score={person.votes}
                                        image={person.image}
                                        type="category"
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

export default LeaderboardPage;
